/**
 * TemplatePreviewPanel — read-only outline preview surfaced via
 * GlobalSidePanel ('templatePreview'). The panel's own header is rendered
 * by GlobalSidePanel via the standard `icon` / `message` / `subMsg` props
 * set by the caller, so this body only renders the section list.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../shared/TailwindComponents/Badge';
import MarkdownRenderer from '../../shared/MarkdownRenderer';
import { Well } from '../../shared/brand/Field';

interface OutlineEntry {
  title?: string;
  section?: string;
  prompt?: string;
  description?: string;
}

interface TemplatePreviewPanelProps {
  outline?: OutlineEntry[];
  description?: string;
}

const TemplatePreviewPanel: React.FC<TemplatePreviewPanelProps> = ({
  outline = [], description,
}) => {
  const { t } = useTranslation();
  return (
    <div className="p-5 space-y-5 bg-bg overflow-y-auto">
      {description && (
        <MarkdownRenderer
          content={description}
          className="text-sm text-fg-body"
        />
      )}

      <div>
        <h4 className="mb-3 text-sm font-semibold capitalize text-fg-strong">
          {t('Sections ({{count}})', { count: outline.length })}
        </h4>
        {outline.length === 0 ? (
          <p className="text-sm italic text-fg-muted">{t('No sections defined.')}</p>
        ) : (
          <ol className="space-y-3">
            {outline.map((entry, idx) => {
              const title = entry.title || entry.section || t('Section {{n}}', { n: idx + 1 });
              const body = entry.prompt || entry.description || '';
              return (
                <li key={`${title}-${idx}`}>
                  <Well className="!p-4 !rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge color="blue">{idx + 1}</Badge>
                      <span className="text-sm font-medium text-fg-strong">
                        {title}
                      </span>
                    </div>
                    {body && (
                      <div className="mt-2">
                        <MarkdownRenderer
                          content={body}
                          className="text-xs text-fg-body"
                        />
                      </div>
                    )}
                  </Well>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default TemplatePreviewPanel;
